import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  extractedDrawingStoreyLabel,
  pickBestPlanPerStorey,
} from '@shared/helpers/project-file-drawing-groups.helpers';
import { formatPlanNumbers } from '@shared/helpers/extracted-storey-key.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { formatStoreyLevel } from '@shared/helpers/storey-level.helpers';
import type {
  ExtractedDrawing,
  PlanAlignment,
} from '@shared/models/project-file-drawing.model';
import type { PlanCoverage } from '@shared/models/storey-level.model';
import type { ProjectFileBatchSnapshot } from '@shared/models/project-file-batch.model';
import { useDrawingCropUrl } from './drawing-crop-preview.hook';
import type { PlanSheet, StoreyPreview } from './plan-storey-previews.helpers';

const REFERENCE_GHOST_OPACITY = 0.45;
const REFERENCE_STACK_COUNT = 3;
const STACK_OVERLAP_LIGHTNESS =
  (1 - REFERENCE_GHOST_OPACITY) ** REFERENCE_STACK_COUNT;
const FOCUSED_REST_SCALE = 0.4;

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  head: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  title: {
    margin: 0,
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.3,
  },
  count: {
    flexShrink: 0,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.3,
  },
  methods: {
    position: 'absolute',
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    zIndex: 4,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
    maxWidth: '70%',
  },
  method: {
    margin: 0,
    padding: theme.spacing(0.25, 1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    backgroundColor: 'rgba(255, 255, 255, 0.94)',
    color: theme.palette.text.secondary,
    font: 'inherit',
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1.4,
    cursor: 'pointer',
  },
  methodOn: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  stage: {
    position: 'relative',
    width: '100%',
    height: 520,
    overflow: 'hidden',
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  stageEmbedded: {
    height: 240,
  },
  metaEmbedded: {
    maxWidth: '58%',
  },
  sheet: {
    position: 'absolute',
    display: 'block',
    boxSizing: 'border-box',
    objectFit: 'fill',
    mixBlendMode: 'multiply',
    pointerEvents: 'none',
  },
  rail: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 3,
    display: 'flex',
    flexDirection: 'row',
  },
  tick: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    height: '100%',
    margin: 0,
    padding: 0,
    border: 'none',
    borderRadius: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      bottom: 0,
      left: 0,
      height: 4,
      backgroundColor: 'rgba(0, 0, 0, 0.22)',
    },
  },
  tickGap: {
    '&::after': {
      height: 0,
      backgroundColor: 'transparent',
      borderBottom: '2px dashed rgba(0, 0, 0, 0.35)',
    },
  },
  tickOn: {
    '&::after': {
      height: 6,
      backgroundColor: theme.palette.primary.main,
    },
  },
  meta: {
    position: 'absolute',
    top: theme.spacing(1.5),
    left: theme.spacing(1.5),
    zIndex: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    maxWidth: 'calc(100% - 360px)',
    padding: theme.spacing(1.25, 1.5),
    pointerEvents: 'none',
    backgroundColor: 'rgba(255, 255, 255, 0.94)',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  metaTitle: {
    margin: 0,
    fontSize: theme.typography.subtitle2.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.3,
  },
  metaSub: {
    margin: 0,
    overflow: 'hidden',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.3,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  facts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.25, 1.75),
    margin: 0,
  },
  fact: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    minWidth: 0,
  },
  factLabel: {
    margin: 0,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1.2,
  },
  factValue: {
    margin: 0,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
  },
}));

type PlanFile = Pick<
  ProjectFileBatchSnapshot['files'][number],
  'id' | 'fileName' | 'extractedDrawings' | 'pageRasters'
>;

type Metres = { width: number; height: number };

const planMetres = (drawing: ExtractedDrawing): Metres | null => {
  if (
    drawing.pixelWidth == null ||
    drawing.pixelHeight == null ||
    drawing.pixelsPerMeter == null
  ) {
    return null;
  }
  return {
    width: drawing.pixelWidth / drawing.pixelsPerMeter,
    height: drawing.pixelHeight / drawing.pixelsPerMeter,
  };
};

const drawingName = (sheet: PlanSheet): string => {
  const printed = sheet.drawing.viewLabel?.trim() ?? '';
  if (printed !== '') return printed;
  return formatPlanNumbers(sheet.drawing.planNumbers);
};

const coverageLabel = (coverage: PlanCoverage | null): string | null => {
  if (coverage === null) return null;
  switch (coverage.kind) {
    case 'remaining':
      return 'Normalplan';
    case 'specific':
      return formatStoreyLevel(coverage.level);
    default:
      return assertUnreachable(coverage);
  }
};

const sheetLabel = (sheet: PlanSheet): string =>
  `${drawingName(sheet)} · ${sheet.fileName}`;

type SheetFact = { label: string; value: string };

const sheetFacts = (sheet: PlanSheet): SheetFact[] => {
  const drawing = sheet.drawing;
  const facts: SheetFact[] = [];
  if (drawing.planNumbers.length > 0) {
    facts.push({
      label: 'Plan',
      value: formatPlanNumbers(drawing.planNumbers),
    });
  }
  const placement = coverageLabel(drawing.coverage);
  if (placement !== null) facts.push({ label: 'Placering', value: placement });
  if (drawing.storeyKind !== 'unknown') {
    facts.push({
      label: 'Våning',
      value: extractedDrawingStoreyLabel(drawing.storeyKind),
    });
  }
  const scale = drawing.scale?.trim() ?? '';
  if (scale !== '') facts.push({ label: 'Skala', value: scale });
  facts.push({ label: 'Sida', value: String(drawing.pageIndex + 1) });
  const drawingId = drawing.drawingId?.trim() ?? '';
  if (drawingId !== '') facts.push({ label: 'Ritning', value: drawingId });
  const building = drawing.buildingLabel?.trim() ?? '';
  if (building !== '') facts.push({ label: 'Hus', value: building });
  return facts;
};

const sheetCountLabel = (count: number): string => {
  if (count === 1) return '1 ritning';
  return `${count} ritningar`;
};

const storeyCountLabel = (count: number): string => {
  if (count === 1) return '1 våning';
  return `${count} våningar`;
};

const byStorey = (a: PlanSheet, b: PlanSheet): number => {
  const ai = a.drawing.planNumbers[0];
  const bi = b.drawing.planNumbers[0];
  if (ai === undefined && bi === undefined) {
    return a.fileName.localeCompare(b.fileName);
  }
  if (ai === undefined) return 1;
  if (bi === undefined) return -1;
  if (ai !== bi) return ai - bi;
  return a.fileName.localeCompare(b.fileName);
};

export type PlanOverlayGroup = {
  key: string;
  label: string;
  sheets: PlanSheet[];
};

export const planOverlayGroups = (
  files: readonly PlanFile[],
): PlanOverlayGroup[] => {
  const buckets = new Map<string, PlanSheet[]>();
  const labels = new Map<string, string>();
  const plans: PlanSheet[] = [];
  for (const file of files) {
    for (const drawing of file.extractedDrawings) {
      if (drawing.drawingKind !== 'plan') continue;
      plans.push({
        fileId: file.id,
        fileName: file.fileName,
        drawing,
        pageRaster:
          (file.pageRasters ?? []).find(
            (raster) => raster.pageIndex === drawing.pageIndex,
          ) ?? null,
      });
    }
  }
  const bestIds = new Set(
    pickBestPlanPerStorey(plans.map((sheet) => sheet.drawing)).map(
      (drawing) => drawing.id,
    ),
  );
  for (const sheet of plans) {
    if (!bestIds.has(sheet.drawing.id)) continue;
    const ids = (sheet.drawing.buildingInstanceIds ?? [])
      .map((id) => id.trim())
      .filter((id) => id !== '');
    const single = sheet.drawing.buildingInstanceId?.trim() ?? '';
    const keys = ids.length > 0 ? ids : [single !== '' ? single : '_'];
    for (const key of keys) {
      const list = buckets.get(key);
      if (list) list.push(sheet);
      else buckets.set(key, [sheet]);
      const label = sheet.drawing.buildingLabel?.trim();
      if (label && !labels.has(key)) labels.set(key, label);
    }
  }
  return [...buckets.entries()]
    .map(([key, sheets]) => ({
      key,
      label: labels.get(key) ?? 'Planritningar',
      sheets: [...sheets].sort(byStorey),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'sv'));
};

const ghostOpacity = (count: number): number => {
  if (count <= 1) return 1;
  return 1 - STACK_OVERLAP_LIGHTNESS ** (1 / count);
};

const sheetOpacity = (
  index: number,
  focus: number | null,
  count: number,
): number => {
  if (focus === index) return 1;
  const ghost = ghostOpacity(count);
  if (focus == null) return ghost;
  return ghost * FOCUSED_REST_SCALE;
};

const PlanSheetImage: React.FC<{
  batchId: string;
  sheet: PlanSheet;
  box: { left: number; top: number; width: number; height: number } | null;
  opacity: number;
  focused: boolean;
}> = ({ batchId, sheet, box, opacity, focused }) => {
  const { classes } = useStyles();
  const url = useDrawingCropUrl(
    batchId,
    sheet.fileId,
    sheet.drawing,
    sheet.pageRaster ?? null,
  );

  if (!url || box == null) return null;
  return (
    <img
      className={classes.sheet}
      src={url}
      alt={sheetLabel(sheet)}
      style={{
        left: box.left,
        top: box.top,
        width: box.width,
        height: box.height,
        opacity,
        outline: focused ? '2px solid rgba(25, 118, 210, 0.9)' : 'none',
      }}
    />
  );
};

type Box = { left: number; top: number; width: number; height: number };

const PLAN_ALIGN_METHODS = [
  { id: 'center', label: 'Centrerad' },
  { id: 'page', label: 'Sida' },
  { id: 'ink', label: 'Bläck' },
  { id: 'combined', label: 'Kombinerad' },
] as const;

type PlanAlignMethod = (typeof PLAN_ALIGN_METHODS)[number]['id'];

const isPlanAlignMethod = (value: string): value is PlanAlignMethod =>
  PLAN_ALIGN_METHODS.some((method) => method.id === value);

const storedAlignment = (
  drawing: ExtractedDrawing,
  method: PlanAlignMethod,
): PlanAlignment | null => {
  if (method === 'center') return null;
  if (drawing.planAlignments) return drawing.planAlignments[method];
  if (method === 'combined') return drawing.planAlignment;
  return null;
};

const sheetsForStack = (
  sheets: readonly PlanSheet[],
  method: PlanAlignMethod,
): PlanSheet[] => {
  if (method === 'center') return [...sheets];
  const aligned = sheets.filter(
    (sheet) => storedAlignment(sheet.drawing, method) != null,
  );
  const first = aligned[0];
  if (first == null) return [...sheets];
  const unit = storedAlignment(first.drawing, method)?.unit;
  return aligned.filter(
    (sheet) => storedAlignment(sheet.drawing, method)?.unit === unit,
  );
};

const alignmentBoxes = (
  stage: { width: number; height: number },
  sheets: readonly PlanSheet[],
  method: PlanAlignMethod,
): Map<string, Box> | null => {
  if (method === 'center') return null;
  if (stage.width <= 0 || stage.height <= 0) return null;
  const items: {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }[] = [];
  for (const sheet of sheets) {
    const alignment = storedAlignment(sheet.drawing, method);
    if (alignment == null) continue;
    if (alignment.unit === 'metre') {
      const metres = planMetres(sheet.drawing);
      if (metres == null) continue;
      items.push({
        id: sheet.drawing.id,
        x: alignment.offsetX,
        y: alignment.offsetY,
        width: metres.width,
        height: metres.height,
      });
      continue;
    }
    if (sheet.drawing.pixelWidth == null || sheet.drawing.pixelHeight == null) {
      continue;
    }
    items.push({
      id: sheet.drawing.id,
      x: alignment.offsetX,
      y: alignment.offsetY,
      width: sheet.drawing.pixelWidth,
      height: sheet.drawing.pixelHeight,
    });
  }
  if (items.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const item of items) {
    minX = Math.min(minX, item.x);
    minY = Math.min(minY, item.y);
    maxX = Math.max(maxX, item.x + item.width);
    maxY = Math.max(maxY, item.y + item.height);
  }
  const spanW = maxX - minX;
  const spanH = maxY - minY;
  if (!(spanW > 0) || !(spanH > 0)) return null;
  const pad = 0.92;
  const scale = Math.min(
    (stage.width * pad) / spanW,
    (stage.height * pad) / spanH,
  );
  const originX = (stage.width - spanW * scale) / 2 - minX * scale;
  const originY = (stage.height - spanH * scale) / 2 - minY * scale;
  const boxes = new Map<string, Box>();
  for (const item of items) {
    boxes.set(item.id, {
      left: originX + item.x * scale,
      top: originY + item.y * scale,
      width: item.width * scale,
      height: item.height * scale,
    });
  }
  return boxes;
};

const fitBox = (
  stage: { width: number; height: number },
  metres: Metres | null,
  maxMetres: Metres | null,
  pixel: { width: number; height: number } | null,
): { left: number; top: number; width: number; height: number } | null => {
  if (stage.width <= 0 || stage.height <= 0) return null;
  const pad = 0.92;
  if (
    metres != null &&
    maxMetres != null &&
    maxMetres.width > 0 &&
    maxMetres.height > 0
  ) {
    const pxPerMetre = Math.min(
      (stage.width * pad) / maxMetres.width,
      (stage.height * pad) / maxMetres.height,
    );
    const width = metres.width * pxPerMetre;
    const height = metres.height * pxPerMetre;
    return {
      width,
      height,
      left: (stage.width - width) / 2,
      top: (stage.height - height) / 2,
    };
  }
  if (pixel == null || pixel.width <= 0 || pixel.height <= 0) return null;
  const scale = Math.min(
    (stage.width * pad) / pixel.width,
    (stage.height * pad) / pixel.height,
  );
  const width = pixel.width * scale;
  const height = pixel.height * scale;
  return {
    width,
    height,
    left: (stage.width - width) / 2,
    top: (stage.height - height) / 2,
  };
};

const slotKindNote = (preview: StoreyPreview): string | null => {
  switch (preview.kind) {
    case 'own':
      return null;
    case 'normalplan':
      return 'Normalplan';
    case 'data_gap':
      return 'Data saknas';
    default:
      return assertUnreachable(preview);
  }
};

const slotDetail = (preview: StoreyPreview): string => {
  const note = slotKindNote(preview);
  if (note == null) return preview.placement;
  return `${preview.placement} · ${note}`;
};

const slotLabel = (preview: StoreyPreview): string => {
  const part =
    preview.partName == null || preview.partName === ''
      ? ''
      : ` · ${preview.partName}`;
  return `${preview.storeyLabel}${part} · ${slotDetail(preview)}`;
};

const sheetAtSlot = (slot: StoreyPreview | null): PlanSheet | null => {
  if (slot == null || slot.kind === 'data_gap') return null;
  return slot.sheet;
};

const sheetAtIndex = (
  list: readonly PlanSheet[],
  index: number | null,
): PlanSheet | null => {
  if (index == null) return null;
  return list[index] ?? null;
};

const SheetMeta: React.FC<{
  sheet: PlanSheet | null;
  countText: string;
  embedded: boolean;
  heading?: string;
  note?: string | null;
}> = ({ sheet, countText, embedded, heading, note }) => {
  const { classes, cx } = useStyles();
  const metaClass = cx(classes.meta, embedded && classes.metaEmbedded);
  const title = heading ?? (sheet == null ? 'Alla plan' : drawingName(sheet));
  if (sheet == null) {
    return (
      <div className={metaClass}>
        <p className={classes.metaTitle}>{title}</p>
        <p className={classes.metaSub}>{note ?? countText}</p>
      </div>
    );
  }
  const facts = sheetFacts(sheet);
  return (
    <div className={metaClass}>
      <p className={classes.metaTitle}>{title}</p>
      <p className={classes.metaSub} title={sheet.fileName}>
        {sheet.fileName}
      </p>
      {note != null && note !== '' && <p className={classes.metaSub}>{note}</p>}
      <dl className={classes.facts}>
        {facts.map((fact) => (
          <div key={fact.label} className={classes.fact}>
            <dt className={classes.factLabel}>{fact.label}</dt>
            <dd className={classes.factValue}>{fact.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const PlanGroupStage: React.FC<{
  batchId: string;
  label: string;
  showLabel: boolean;
  sheets: readonly PlanSheet[];
  method: PlanAlignMethod;
  onMethodClick: React.MouseEventHandler<HTMLButtonElement>;
  embedded: boolean;
  /**
   * One tick per våning-table row. Omit to tick each sheet, for plans with
   * no known storeys.
   */
  slots?: readonly StoreyPreview[];
}> = ({
  batchId,
  label,
  showLabel,
  sheets,
  method,
  onMethodClick,
  embedded,
  slots,
}) => {
  const { classes, cx } = useStyles();
  const stageRef = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState({ width: 0, height: 0 });
  const [focus, setFocus] = useState<{
    method: PlanAlignMethod;
    index: number;
  } | null>(null);
  const focusIndex = focus?.method === method ? focus.index : null;

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry == null) return;
      setStage({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const stacked = useMemo(
    () => sheetsForStack(sheets, method),
    [sheets, method],
  );
  const placed = useMemo(
    () => alignmentBoxes(stage, stacked, method),
    [stage, stacked, method],
  );
  const awaitingAlignment =
    method !== 'center' &&
    stacked.some((sheet) => storedAlignment(sheet.drawing, method) != null) &&
    placed == null;

  const maxMetres = useMemo(() => {
    let width = 0;
    let height = 0;
    let any = false;
    for (const sheet of stacked) {
      const metres = planMetres(sheet.drawing);
      if (metres == null) continue;
      any = true;
      width = Math.max(width, metres.width);
      height = Math.max(height, metres.height);
    }
    if (!any) return null;
    return { width, height };
  }, [stacked]);

  type SegmentEnterHandler = React.MouseEventHandler<HTMLButtonElement>;
  const onSegmentEnter = useCallback<SegmentEnterHandler>(
    (event) => {
      const index = Number(event.currentTarget.dataset.index);
      if (Number.isNaN(index)) return;
      setFocus({ method, index });
    },
    [method],
  );

  type RailLeaveHandler = React.MouseEventHandler<HTMLDivElement>;
  const onRailLeave = useCallback<RailLeaveHandler>(() => {
    setFocus(null);
  }, []);

  const slotMode = slots != null;
  const focusedSlot =
    slots != null && focusIndex != null ? (slots[focusIndex] ?? null) : null;
  const focusSheet = slotMode
    ? sheetAtSlot(focusedSlot)
    : sheetAtIndex(stacked, focusIndex);
  const title = showLabel ? label : 'Planritningar';
  const countText =
    slots == null
      ? sheetCountLabel(stacked.length)
      : storeyCountLabel(slots.length);
  const opacityForSheet = (sheet: PlanSheet, index: number): number => {
    if (!slotMode) return sheetOpacity(index, focusIndex, stacked.length);
    const ghost = ghostOpacity(stacked.length);
    if (focusIndex == null) return ghost;
    if (focusSheet != null && focusSheet.drawing.id === sheet.drawing.id) {
      return 1;
    }
    return ghost * FOCUSED_REST_SCALE;
  };
  const sheetIsFocused = (sheet: PlanSheet, index: number): boolean => {
    if (!slotMode) return focusIndex === index;
    return focusSheet != null && focusSheet.drawing.id === sheet.drawing.id;
  };

  return (
    <section className={classes.group}>
      <div className={classes.head}>
        {(!embedded || showLabel) && <h3 className={classes.title}>{title}</h3>}
        <span className={classes.count}>{countText}</span>
      </div>
      <div
        ref={stageRef}
        className={cx(classes.stage, embedded && classes.stageEmbedded)}
      >
        {stacked.map((sheet, index) => {
          const metres = planMetres(sheet.drawing);
          const pixel =
            sheet.drawing.pixelWidth != null &&
            sheet.drawing.pixelHeight != null
              ? {
                  width: sheet.drawing.pixelWidth,
                  height: sheet.drawing.pixelHeight,
                }
              : null;
          const fitted = fitBox(stage, metres, maxMetres, pixel);
          const fromAlignment = placed?.get(sheet.drawing.id) ?? null;
          const box =
            placed != null || awaitingAlignment ? fromAlignment : fitted;
          return (
            <PlanSheetImage
              key={sheet.drawing.id}
              batchId={batchId}
              sheet={sheet}
              box={box}
              opacity={opacityForSheet(sheet, index)}
              focused={sheetIsFocused(sheet, index)}
            />
          );
        })}
        <div className={classes.rail} onMouseLeave={onRailLeave}>
          {slots == null
            ? stacked.map((sheet, index) => (
                <button
                  key={sheet.drawing.id}
                  type="button"
                  className={cx(
                    classes.tick,
                    focusIndex === index && classes.tickOn,
                  )}
                  data-index={index}
                  title={sheetLabel(sheet)}
                  aria-label={sheetLabel(sheet)}
                  onMouseEnter={onSegmentEnter}
                />
              ))
            : slots.map((slot, index) => (
                <button
                  key={slot.key}
                  type="button"
                  className={cx(
                    classes.tick,
                    slot.kind === 'data_gap' && classes.tickGap,
                    focusIndex === index && classes.tickOn,
                  )}
                  data-index={index}
                  title={slotLabel(slot)}
                  aria-label={slotLabel(slot)}
                  onMouseEnter={onSegmentEnter}
                />
              ))}
        </div>
        <SheetMeta
          sheet={focusSheet}
          countText={countText}
          embedded={embedded}
          heading={focusedSlot == null ? undefined : focusedSlot.storeyLabel}
          note={focusedSlot == null ? null : slotDetail(focusedSlot)}
        />
        <div className={classes.methods}>
          {PLAN_ALIGN_METHODS.map((item) => (
            <button
              key={item.id}
              type="button"
              data-method={item.id}
              className={cx(
                classes.method,
                method === item.id && classes.methodOn,
              )}
              onClick={onMethodClick}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export const PlanOverlayStages: React.FC<{
  batchId: string;
  groups: readonly PlanOverlayGroup[];
  embedded?: boolean;
  /**
   * One slot per våning-table row, in that order. Omit when the plans have
   * no known storeys.
   */
  storeyPreviews?: readonly StoreyPreview[];
}> = ({ batchId, groups, embedded = false, storeyPreviews }) => {
  const { classes } = useStyles();
  const [method, setMethod] = useState<PlanAlignMethod>('combined');
  type MethodClick = React.MouseEventHandler<HTMLButtonElement>;
  const onMethodClick = useCallback<MethodClick>((event) => {
    const next = event.currentTarget.dataset.method ?? '';
    if (!isPlanAlignMethod(next)) return;
    setMethod(next);
  }, []);
  const storeySlots =
    storeyPreviews == null || storeyPreviews.length === 0
      ? null
      : storeyPreviews;
  if (groups.length === 0 && storeySlots == null) return null;
  if (storeySlots != null) {
    const only = groups.length === 1 ? groups[0] : undefined;
    return (
      <div className={classes.root}>
        <PlanGroupStage
          batchId={batchId}
          label={only == null ? 'Planritningar' : only.label}
          showLabel={!embedded}
          sheets={groups.flatMap((group) => group.sheets)}
          slots={storeySlots}
          method={method}
          onMethodClick={onMethodClick}
          embedded={embedded}
        />
      </div>
    );
  }
  const showLabel = !embedded || groups.length > 1;
  return (
    <div className={classes.root}>
      {groups.map((group) => (
        <PlanGroupStage
          key={group.key}
          batchId={batchId}
          label={group.label}
          showLabel={showLabel}
          sheets={group.sheets}
          method={method}
          onMethodClick={onMethodClick}
          embedded={embedded}
        />
      ))}
    </div>
  );
};

export const PlanOverlayStack: React.FC<{
  batchId: string;
  files: readonly PlanFile[];
}> = ({ batchId, files }) => {
  const groups = useMemo(() => planOverlayGroups(files), [files]);
  return <PlanOverlayStages batchId={batchId} groups={groups} />;
};
